<template>
  <div class="card shadow-sm table-card mb-4">
    <div class="form-card-header flex-wrap">
      <div class="section-number"><i class="fe fe-trending-up" style="font-size: 12px;"></i></div>
      <div class="flex-grow-1">
        <h6 class="mb-0 font-weight-bold text-white">Enrolment trends</h6>
        <small style="color: rgba(255,255,255,.75);">New enrollees per {{ period.granularity }}, by programme</small>
      </div>
    </div>
    <div class="card-body">
      <div class="d-flex flex-wrap align-items-center mb-3" style="gap: 8px;">
        <PeriodPicker v-model="period" with-granularity @change="load" />
        <div class="btn-group btn-group-sm" role="group" aria-label="Who">
          <button v-for="w in whoOptions" :key="w.key" type="button" class="btn" :class="who === w.key ? 'btn-dark' : 'btn-outline-dark'" @click="who = w.key; load()">{{ w.label }}</button>
        </div>
        <v-select class="sector-select" multiple v-model="sectors" :options="sectorOptions" placeholder="All programmes" @input="load" />
        <div class="btn-group btn-group-sm ml-auto" role="group" aria-label="Chart">
          <button type="button" class="btn" :class="view === 'sector' ? 'btn-secondary' : 'btn-outline-secondary'" @click="view = 'sector'">By programme</button>
          <button type="button" class="btn" :class="view === 'kind' ? 'btn-secondary' : 'btn-outline-secondary'" @click="view = 'kind'">Principals / dependants</button>
        </div>
      </div>

      <div v-if="loading && !data" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
      <div v-else-if="error" class="empty-state"><i class="fe fe-alert-triangle"></i>{{ error }}</div>
      <template v-else-if="data">
        <div class="row">
          <div class="col-lg col-md-4 col-6" v-for="tile in tiles" :key="tile.label">
            <div class="stat-card">
              <div class="stat-icon-wrap" :style="{ background: tile.bg }"><i :class="tile.icon" :style="{ color: tile.color }"></i></div>
              <div><p class="stat-label">{{ tile.label }}</p><h3 class="stat-value">{{ tile.value }}</h3><p class="stat-sub" :class="tile.subClass">{{ tile.sub }}</p></div>
            </div>
          </div>
        </div>
        <div :style="{ opacity: loading ? 0.5 : 1 }">
          <apexchart type="bar" height="320" :options="barOptions" :series="barSeries" :key="'bar' + chartKey" />
          <h6 class="font-weight-bold mt-3 mb-0 small text-uppercase text-muted">Total enrolled over time</h6>
          <apexchart type="area" height="180" :options="cumulativeOptions" :series="cumulativeSeries" :key="'cum' + chartKey" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import PeriodPicker from "./PeriodPicker.vue";

/** Enrolment trends (GET analytics/enrollment/trends), weekly / monthly / yearly, filterable by programme. */
export default {
  name: "EnrollmentTrends",
  components: { PeriodPicker },
  data() {
    return {
      period: { from: "", to: "", granularity: "month" },
      who: "both",
      sectors: [],
      sectorOptions: [],
      view: "sector",
      data: null,
      loading: false,
      error: "",
      chartKey: 0,
      whoOptions: [{ key: "both", label: "Everyone" }, { key: "principals", label: "Principals" }, { key: "dependants", label: "Dependants" }],
      palette: ["#2563eb", "#059669", "#d97706", "#7c3aed", "#db2777", "#0891b2", "#94a3b8"],
    };
  },
  computed: {
    s() { return this.data.summary; },
    tiles() {
      const s = this.s;
      const growth = s.growth_pct;
      return [
        { label: "Enrolled in period", value: s.total.toLocaleString(), sub: growth === null ? "No earlier data to compare" : `${growth >= 0 ? "▲" : "▼"} ${Math.abs(growth)}% vs previous period`, subClass: growth === null ? "" : growth >= 0 ? "delta-down" : "delta-up", icon: "fe fe-user-plus", bg: "#eff6ff", color: "#2563eb" },
        { label: "Principals / dependants", value: `${s.principals.toLocaleString()} / ${s.dependants.toLocaleString()}`, sub: s.total ? `${Math.round((100 * s.dependants) / s.total)}% dependants` : "—", icon: "fe fe-users", bg: "#f5f3ff", color: "#7c3aed" },
        { label: `Average per ${this.data.granularity}`, value: s.average_per_period.toLocaleString(), sub: `${this.data.categories.length} ${this.data.granularity}s`, icon: "fe fe-bar-chart", bg: "#ecfdf5", color: "#059669" },
        { label: "Best period", value: s.best_period ? s.best_period.total.toLocaleString() : "—", sub: s.best_period ? s.best_period.label : "", icon: "fe fe-award", bg: "#fff7ed", color: "#ea580c" },
        { label: "Total enrolled", value: s.enrolled_at_end.toLocaleString(), sub: `at the end of the period`, icon: "fe fe-database", bg: "#f0fdfa", color: "#0d9488" },
      ];
    },
    barSeries() {
      if (this.view === "kind") {
        return [{ name: "Principals", data: this.data.series.principals }, { name: "Dependants", data: this.data.series.dependants }];
      }
      return this.data.by_sector.map((s) => ({ name: s.name, data: s.data }));
    },
    barOptions() {
      return {
        chart: { stacked: true, toolbar: { show: true }, animations: { enabled: true } },
        colors: this.palette,
        plotOptions: { bar: { columnWidth: this.data.categories.length > 30 ? "90%" : "60%", borderRadius: 3 } },
        xaxis: { categories: this.data.categories, labels: { rotate: -45, hideOverlappingLabels: true } },
        yaxis: { labels: { formatter: (v) => Math.round(v).toLocaleString() }, title: { text: "New enrollees" } },
        dataLabels: { enabled: true },
        legend: { position: "top" },
        tooltip: { shared: true, intersect: false, y: { formatter: (v) => (v || 0).toLocaleString() } },
      };
    },
    cumulativeSeries() {
      return [{ name: "Total enrolled", data: this.data.cumulative }];
    },
    cumulativeOptions() {
      return {
        chart: { toolbar: { show: false }, sparkline: { enabled: false } },
        colors: ["#0d9488"],
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
        xaxis: { categories: this.data.categories, labels: { show: false } },
        yaxis: { labels: { formatter: (v) => Math.round(v).toLocaleString() } },
        dataLabels: { enabled: true },
        tooltip: { y: { formatter: (v) => (v || 0).toLocaleString() } },
      };
    },
  },
  mounted() {
    this.$batchGet("/api/v1/auth/analytics/enrollment/sectors").then((res) => {
      this.sectorOptions = (res.data.data.rows || []).map((r) => r.sector);
    }).catch(() => {});
    if (this.period.from) this.load();
  },
  methods: {
    load() {
      if (!this.period.from) return;
      this.loading = true;
      this.error = "";
      this.$batchGet("/api/v1/auth/analytics/enrollment/trends", {
        params: { from: this.period.from, to: this.period.to, granularity: this.period.granularity, who: this.who, sectors: this.sectors },
      })
        .then((res) => { this.data = res.data.data; this.chartKey++; })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load enrolment trends."; })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
.sector-select { min-width: 220px; max-width: 420px; font-size: 0.85rem; }
</style>
